import { useEffect } from 'react'
import mapboxgl from 'mapbox-gl'
import {
  getAllDemographicsLayerIds,
  createDemographicsFillColorExpression,
  DEMOGRAPHICS_LAYER_ORDER,
  getDemographicsField,
  formatDemographicsValue,
  getDemographicsLayerDisplayName,
} from '../../../../molecules/AnalysisMap/utils/demographicsLayerHelpers'
import { getLayerConfig } from '../../../../molecules/AnalysisMap/config'
import { API_BASE_URL } from '../../../../../config'

interface UseDemographicsLayersProps {
  map: React.MutableRefObject<mapboxgl.Map | null>
  mapLoaded: boolean
  popup: React.MutableRefObject<mapboxgl.Popup | null>
  styleLoadedCount: number
  otherMapOptions: any
}

/**
 * Hook to handle demographics choropleth layers and hover tooltips
 */
export const useDemographicsLayers = ({
  map,
  mapLoaded,
  popup,
  styleLoadedCount,
  otherMapOptions,
}: UseDemographicsLayersProps) => {
  // Handle demographics choropleth layers (vector tiles)
  useEffect(() => {
    if (!map.current || !mapLoaded) {
      return
    }

    // This prop will be passed from AnalysisMap when demographics section is active
    const demographicsSection =
      (otherMapOptions as any).activeSection === 'demographics'
    const demographicsLayerStates = (otherMapOptions as any)
      .demographicsLayerStates

    if (!demographicsSection || !demographicsLayerStates) {
      // Remove demographics source and layers if not in demographics section
      let existingDemographicsLayers: string[] = []
      try {
        const style = map.current.getStyle()
        if (style?.layers) {
          existingDemographicsLayers = style.layers
            .filter((layer) => layer.id.startsWith('demographics-'))
            .map((layer) => layer.id)
        }
      } catch (error) {
        // Style not loaded yet, skip cleanup
        return
      }

      existingDemographicsLayers.forEach((layerId) => {
        if (map.current!.getLayer(layerId)) {
          try {
            map.current!.removeLayer(layerId)
          } catch (error) {
            console.warn(
              `Failed to remove demographics layer ${layerId}:`,
              error
            )
          }
        }
      })

      if (map.current!.getSource('demographics-vector-tiles')) {
        try {
          map.current!.removeSource('demographics-vector-tiles')
        } catch (error) {
          console.warn('Failed to remove demographics source:', error)
        }
      }

      return
    }

    // Demographics section is active - add vector tile source and layers
    // Add vector tile source if it doesn't exist
    if (!map.current!.getSource('demographics-vector-tiles')) {
      map.current!.addSource('demographics-vector-tiles', {
        type: 'vector',
        tiles: [`${API_BASE_URL}/demographics/vector-tiles/{z}/{x}/{y}`],
        minzoom: 10,
        maxzoom: 16,
      })
    }

    // Add or update fill layers for each demographics layer
    const layerIds = getAllDemographicsLayerIds()

    layerIds.forEach((layerId: string) => {
      const layerConfig = getLayerConfig(layerId)
      const layerState = demographicsLayerStates[layerId] || {
        enabled: layerConfig?.defaultEnabled || false,
        opacity: layerConfig?.defaultOpacity || 70,
      }

      if (!map.current!.getLayer(layerId)) {
        // Add new fill layer

        const fillColorExpression = createDemographicsFillColorExpression(
          layerId,
          layerConfig
        )

        map.current!.addLayer({
          id: layerId,
          type: 'fill',
          source: 'demographics-vector-tiles',
          'source-layer': 'hits', // searchMvt returns polygons in 'hits' layer
          layout: {
            visibility: layerState.enabled ? 'visible' : 'none',
          },
          paint: {
            'fill-outline-color': 'transparent',
            'fill-color': fillColorExpression,
            'fill-opacity': layerState.opacity / 100,
          },
        })
      } else {
        // Update existing layer visibility and opacity
        map.current!.setLayoutProperty(
          layerId,
          'visibility',
          layerState.enabled ? 'visible' : 'none'
        )
        map.current!.setPaintProperty(
          layerId,
          'fill-opacity',
          layerState.opacity / 100
        )
      }
    })

    // Apply Z-ordering to ensure proper layer stacking
    DEMOGRAPHICS_LAYER_ORDER.forEach((layerId: string, index: number) => {
      if (map.current!.getLayer(layerId) && index > 0) {
        const previousLayerId = DEMOGRAPHICS_LAYER_ORDER[index - 1]
        if (map.current!.getLayer(previousLayerId)) {
          try {
            map.current!.moveLayer(layerId, previousLayerId)
          } catch (error) {
            // Layer ordering might fail if layers don't exist yet, that's ok
          }
        }
      }
    })
  }, [mapLoaded, styleLoadedCount, otherMapOptions])

  // Setup hover tooltips for demographics layers
  useEffect(() => {
    if (!map.current || !mapLoaded) return

    // Get all enabled demographics layers
    const enabledDemographicsLayers = getAllDemographicsLayerIds().filter(
      (layerId) => {
        const layerState = otherMapOptions.demographicsLayerStates?.[layerId]
        return layerState?.enabled && map.current!.getLayer(layerId)
      }
    )

    if (enabledDemographicsLayers.length === 0) return

    // Find the topmost visible layer (based on Z-order)
    const topmostLayer = DEMOGRAPHICS_LAYER_ORDER.slice()
      .reverse()
      .find((layerId) => enabledDemographicsLayers.includes(layerId))

    if (!topmostLayer) return

    const field = getDemographicsField(topmostLayer)
    if (!field) return

    // Handle mouse move to show tooltip
    const handleMouseMove = (e: mapboxgl.MapMouseEvent) => {
      const features = map.current!.queryRenderedFeatures(e.point, {
        layers: [topmostLayer],
      })

      if (features.length > 0 && popup.current) {
        const feature = features[0]
        const value = feature.properties?.[field]
        const formattedValue = formatDemographicsValue(topmostLayer, value)
        const layerName = getDemographicsLayerDisplayName(topmostLayer)

        // Change cursor to pointer
        map.current!.getCanvas().style.cursor = 'pointer'

        // Show popup with formatted value
        popup.current
          .setLngLat(e.lngLat)
          .setHTML(
            `<div style="padding: 8px 12px; font-size: 13px;">
              <div style="font-weight: 600; margin-bottom: 4px;">${layerName}</div>
              <div>${formattedValue}</div>
            </div>`
          )
          .addTo(map.current!)
      } else {
        // Reset cursor
        map.current!.getCanvas().style.cursor = ''
        popup.current?.remove()
      }
    }

    // Handle mouse leave to hide tooltip
    const handleMouseLeave = () => {
      map.current!.getCanvas().style.cursor = ''
      popup.current?.remove()
    }

    // Add event listeners
    map.current.on('mousemove', topmostLayer, handleMouseMove)
    map.current.on('mouseleave', topmostLayer, handleMouseLeave)

    // Cleanup
    return () => {
      if (map.current) {
        map.current.off('mousemove', topmostLayer, handleMouseMove)
        map.current.off('mouseleave', topmostLayer, handleMouseLeave)
      }
      popup.current?.remove()
    }
  }, [mapLoaded, otherMapOptions.demographicsLayerStates])
}
